import VueRouter, { Route } from 'vue-router'
import { Store } from 'vuex'
import {
  buildEcommercePayload,
  getGa4ItemPrice,
  getItemListContext,
  mapGa4Item,
  mapGa4Items
} from './ga4'

const PRODUCT_ROUTE_NAMES = [
  'product',
  'simple-product',
  'configurable-product',
  'bundle-product',
  'virtual-product',
  'grouped-product',
  'downloadable-product'
]

const LOCATION_WAIT_MS = 2000
const DATA_WAIT_MS = 4000

export interface PageContext {
  page_location: string,
  page_path: string,
  page_title: string
}

function nextFrame (): Promise<void> {
  return new Promise(resolve => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setTimeout(resolve, 16)
      return
    }
    window.requestAnimationFrame(() => resolve())
  })
}

function sleep (ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function waitUntil (
  predicate: () => boolean,
  timeoutMs: number,
  isCancelled: () => boolean
): Promise<boolean> {
  const startedAt = Date.now()

  while (Date.now() - startedAt < timeoutMs) {
    if (isCancelled()) return false
    if (predicate()) return true
    await nextFrame()
  }

  return !isCancelled() && predicate()
}

export function normalizePath (path: string = ''): string {
  let normalized = String(path).split('?')[0].split('#')[0]
  if (!normalized) return '/'
  if (!normalized.startsWith('/')) normalized = `/${normalized}`
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1)
  }
  return normalized
}

export function getPageContext (): PageContext {
  if (typeof window === 'undefined') {
    return { page_location: '', page_path: '', page_title: '' }
  }

  return {
    page_location: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`,
    page_title: document.title || ''
  }
}

function locationMatchesRoute (route: Route): boolean {
  if (typeof window === 'undefined' || !route) return false
  return normalizePath(window.location.pathname) === normalizePath(route.path)
}

function urlCandidates (entity: any): string[] {
  return [entity?.url_path, entity?.url_key, entity?.slug]
    .filter(Boolean)
    .map((value: string) => normalizePath(`/${String(value).replace(/^\//, '')}`))
}

function pathMatchesEntity (entity: any): boolean {
  if (typeof window === 'undefined' || !entity) return false
  const path = normalizePath(window.location.pathname)
  return urlCandidates(entity).some(candidate => (
    path === candidate ||
    path === `${candidate}.html` ||
    path.endsWith(candidate)
  ))
}

function routeBaseName (route: Route): string {
  const name = String(route?.name || '')
  if (name.startsWith('urldispatcher-')) return name
  const parts = name.split('-')
  if (parts.length > 1 && parts[0].length <= 5) {
    return parts.slice(1).join('-')
  }
  return name
}

export function isProductRoute (route: Route): boolean {
  if (!route) return false
  const name = routeBaseName(route)
  if (PRODUCT_ROUTE_NAMES.includes(name)) return true
  if (name.endsWith('-product')) return true
  return Boolean(route.params?.parentSku || route.params?.childSku)
}

export function isCategoryRoute (route: Route, store: Store<any>): boolean {
  if (!route) return false
  const name = routeBaseName(route)
  if (name === 'category' || name.endsWith('-category')) return true
  if (isProductRoute(route)) return false
  return pathMatchesEntity(store.getters['category-next/getCurrentCategory'])
}

export function isHomeRoute (route: Route): boolean {
  const name = routeBaseName(route)
  if (name === 'home') return true
  return normalizePath(route?.path) === '/'
}

function isProductDataComplete (product: any): boolean {
  return Boolean(product && product.sku && product.name)
}

function productMatchesRoute (product: any, route: Route): boolean {
  if (!isProductDataComplete(product)) return false
  if (pathMatchesEntity(product)) return true

  const parentSku = route?.params?.parentSku
  const childSku = route?.params?.childSku
  if (!parentSku && !childSku) return false

  return (
    product.sku === parentSku ||
    product.sku === childSku ||
    product.parentSku === parentSku ||
    String(product.id) === String(parentSku)
  )
}

function listFingerprint (products: any[] = [], startIndex = 0): string {
  const skus = (products || []).slice(0, 12).map(product => product?.sku).join(',')
  return `${startIndex}::${skus}`
}

function productViewKey (product: any): string {
  const path = typeof window !== 'undefined' ? normalizePath(window.location.pathname) : ''
  const sku = product?.parentSku || product?.sku || ''
  return `${path}::${sku}`
}

export function createPageTracker ({
  GTM,
  store,
  router,
  currencyCode
}: {
  GTM: any,
  store: Store<any>,
  router: VueRouter,
  currencyCode: string
}) {
  let navigationId = 0
  let lastContentViewNav = 0
  let lastProductViewKey: string | null = null
  let lastListKey: string | null = null
  let lastRemarketingKey: string | null = null

  const isCancelled = (nav: number) => nav !== navigationId

  const pushContentView = (route: Route, page: PageContext) => {
    if (lastContentViewNav === navigationId) return
    lastContentViewNav = navigationId

    const dataLayer = ((window as any).dataLayer = (window as any).dataLayer || [])
    dataLayer.push({
      event: 'content-view',
      'content-name': page.page_path,
      'content-view-name': route.name || route.path,
      page_location: page.page_location,
      page_path: page.page_path,
      page_title: page.page_title
    })
  }

  const trackRemarketing = (params: Record<string, any>, page: PageContext) => {
    const key = `${page.page_path}::${JSON.stringify(params)}`
    if (key === lastRemarketingKey) return
    lastRemarketingKey = key

    GTM.trackEvent({
      event: 'remarketing',
      page_location: page.page_location,
      page_path: page.page_path,
      google_tag_params: params
    })
  }

  const trackViewItem = (product: any, page: PageContext) => {
    const key = productViewKey(product)
    if (!product || key === lastProductViewKey) return
    lastProductViewKey = key

    const item = mapGa4Item(product, store)
    const value = getGa4ItemPrice(product)

    const dataLayer = (window as any).dataLayer
    if (Array.isArray(dataLayer)) {
      dataLayer.push({ ecommerce: null })
    }

    GTM.trackEvent({
      event: 'view_item',
      page_location: page.page_location,
      page_path: page.page_path,
      ecommerce: buildEcommercePayload(currencyCode, [item], { value })
    })
  }

  const trackViewItemList = (products: any[] = [], startIndex = 0) => {
    if (!Array.isArray(products) || !products.length) return
    if (typeof window !== 'undefined' && !locationMatchesRoute(router.currentRoute)) return

    const page = getPageContext()
    const fingerprint = `${page.page_path}::${listFingerprint(products, startIndex)}`
    if (fingerprint === lastListKey) return
    lastListKey = fingerprint

    const listContext = getItemListContext(store)
    const items = mapGa4Items(products, store, listContext, startIndex)

    const dataLayer = (window as any).dataLayer
    if (Array.isArray(dataLayer)) {
      dataLayer.push({ ecommerce: null })
    }

    GTM.trackEvent({
      event: 'view_item_list',
      page_location: page.page_location,
      page_path: page.page_path,
      ecommerce: buildEcommercePayload(currencyCode, items, listContext)
    })
  }

  const fireProductPage = (route: Route, product: any) => {
    const page = getPageContext()
    pushContentView(route, page)
    trackViewItem(product, page)
    trackRemarketing({
      ecomm_pagetype: 'product',
      ecomm_prodid: [product.sku],
      ecomm_pname: [product.name],
      ecomm_pcat: [mapGa4Item(product, store).item_category].filter(Boolean),
      ecomm_totalvalue: getGa4ItemPrice(product)
    }, page)
  }

  const fireCategoryPage = (route: Route) => {
    const page = getPageContext()
    const category = store.getters['category-next/getCurrentCategory']
    const products = store.getters['category-next/getCategoryProducts'] || []

    pushContentView(route, page)
    trackViewItemList(products)
    trackRemarketing({
      ecomm_pagetype: 'category',
      ecomm_pcat: category?.name
    }, page)
  }

  const fireGenericPage = (route: Route) => {
    const page = getPageContext()
    pushContentView(route, page)

    if (isHomeRoute(route)) {
      trackRemarketing({ ecomm_pagetype: 'home' }, page)
    }
  }

  const settleAndTrack = async (route: Route, nav: number) => {
    if (typeof window === 'undefined' || !route) return

    const locationReady = await waitUntil(
      () => locationMatchesRoute(route),
      LOCATION_WAIT_MS,
      () => isCancelled(nav)
    )
    if (isCancelled(nav)) return

    await sleep(0)
    await nextFrame()
    if (isCancelled(nav)) return

    const likelyProduct = isProductRoute(route) || pathMatchesEntity(store.state.product?.current)
    const likelyCategory = !likelyProduct && isCategoryRoute(route, store)

    if (likelyProduct) {
      const productReady = await waitUntil(
        () => productMatchesRoute(store.state.product?.current, route),
        DATA_WAIT_MS,
        () => isCancelled(nav)
      )
      if (isCancelled(nav)) return

      const product = store.state.product?.current
      if (productReady && productMatchesRoute(product, route)) {
        fireProductPage(route, product)
      }
      // If the product is not ready yet, SET_CURRENT retries via onProductUpdated.
      return
    }

    if (likelyCategory || pathMatchesEntity(store.getters['category-next/getCurrentCategory'])) {
      await waitUntil(
        () => pathMatchesEntity(store.getters['category-next/getCurrentCategory']),
        DATA_WAIT_MS,
        () => isCancelled(nav)
      )
      if (isCancelled(nav)) return
      fireCategoryPage(route)
      return
    }

    if (!isHomeRoute(route)) {
      const lateProduct = await waitUntil(
        () => productMatchesRoute(store.state.product?.current, route),
        300,
        () => isCancelled(nav)
      )
      if (isCancelled(nav)) return
      if (lateProduct) {
        fireProductPage(route, store.state.product.current)
        return
      }

      const lateCategory = await waitUntil(
        () => pathMatchesEntity(store.getters['category-next/getCurrentCategory']),
        300,
        () => isCancelled(nav)
      )
      if (isCancelled(nav)) return
      if (lateCategory) {
        fireCategoryPage(route)
        return
      }
    }

    if (isCancelled(nav) || !locationReady) return
    fireGenericPage(route)
  }

  const trackRoute = (route: Route) => {
    if (typeof window === 'undefined' || !route) return
    navigationId += 1
    const nav = navigationId
    const nextPath = normalizePath(route.path)
    const lastPath = lastProductViewKey ? lastProductViewKey.split('::')[0] : null
    if (lastPath && lastPath !== nextPath) {
      lastProductViewKey = null
    }
    settleAndTrack(route, nav)
  }

  const onProductUpdated = () => {
    const route = router.currentRoute
    if (!isProductRoute(route) && !pathMatchesEntity(store.state.product?.current)) return
    if (!locationMatchesRoute(route)) return
    settleAndTrack(route, navigationId)
  }

  const onCategoryProductsSet = (products: any[] = []) => {
    const route = router.currentRoute
    if (!locationMatchesRoute(route)) return
    if (!isCategoryRoute(route, store) && !pathMatchesEntity(store.getters['category-next/getCurrentCategory'])) return
    trackViewItemList(products)
  }

  const onCategoryProductsAdd = (addedProducts: any[] = []) => {
    const route = router.currentRoute
    if (!locationMatchesRoute(route)) return
    const currentProducts = store.getters['category-next/getCategoryProducts'] || []
    const startIndex = Math.max(currentProducts.length - addedProducts.length, 0)
    trackViewItemList(addedProducts, startIndex)
  }

  return {
    trackRoute,
    onProductUpdated,
    onCategoryProductsSet,
    onCategoryProductsAdd
  }
}
